import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const API_URL = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

export async function fetchApi(endpoint, options = {}) {
  const token = localStorage.getItem('vyta_token');
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    // Map common errors to user-friendly messages
    let message = error.detail || 'Request failed';
    if (message === 'Invalid credentials') {
      message = 'Incorrect email or password. Please try again.';
    } else if (message === 'Email already registered') {
      message = 'This email is already in use. Please log in or use a different email.';
    } else if (message === 'Invalid token') {
      message = 'Your session has expired. Please log in again.';
    } else if (message.includes('No such customer')) {
      message = 'Payment setup issue. Please try again.';
    }
    throw new Error(message);
  }
  
  return response.json();
}

export function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

export function formatTime(dateString) {
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function calculateAge(dob) {
  const today = new Date();
  const birthDate = new Date(dob);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
}

export const GOALS = [
  { id: 'weight_loss', label: 'Weight Loss', icon: 'Scale' },
  { id: 'strength', label: 'Strength Increase', icon: 'Dumbbell' },
  { id: 'cardio', label: 'Cardiovascular Fitness', icon: 'Heart' },
  { id: '10k', label: '10km Run', icon: 'Timer' },
  { id: 'marathon', label: 'Marathon', icon: 'Trophy' },
  { id: 'ultra', label: 'Ultra Marathon', icon: 'Mountain' },
  { id: 'afl', label: 'AFL Performance', icon: 'Target' },
  { id: 'soccer', label: 'Soccer Performance', icon: 'Target' },
  { id: 'general', label: 'General Fitness', icon: 'Activity' },
  { id: 'custom', label: 'Custom Goal', icon: 'Plus' },
];

export const EQUIPMENT = [
  { id: 'dumbbells', label: 'Dumbbells' },
  { id: 'kettlebells', label: 'Kettlebells' },
  { id: 'bands', label: 'Resistance Bands' },
  { id: 'barbell', label: 'Barbell' },
  { id: 'rack', label: 'Squat Rack' },
  { id: 'cardio', label: 'Cardio Machines' },
  { id: 'none', label: 'No Equipment' },
];

export const ALLERGIES = [
  'Lactose',
  'Gluten',
  'Nuts',
  'Eggs',
  'Soy',
  'Shellfish',
  'Fish',
  'Sesame',
  'FODMAP',
  'Other'
];

export const BODY_TYPES = [
  { id: 'ectomorph', label: 'Ectomorph', description: 'Lean and long, difficulty building muscle' },
  { id: 'mesomorph', label: 'Mesomorph', description: 'Athletic build, gains muscle easily' },
  { id: 'endomorph', label: 'Endomorph', description: 'Broader build, gains weight easily' },
];

export const EXPERIENCE_LEVELS = [
  { id: 'beginner', label: 'Beginner', description: 'New to training (0-1 years)' },
  { id: 'intermediate', label: 'Intermediate', description: 'Regular training (1-3 years)' },
  { id: 'advanced', label: 'Advanced', description: 'Consistent training (3+ years)' },
];

export const DIETARY_PREFERENCES = [
  'No Preference',
  'Vegetarian',
  'Vegan',
  'Pescatarian',
  'Keto',
  'Paleo',
  'Mediterranean',
  'Low Carb',
  'High Protein'
];

export const CUISINES = [
  { id: 'western', label: 'Western/International', countries: ['United States', 'USA', 'Canada', 'United Kingdom', 'UK', 'Australia', 'New Zealand', 'Ireland'] },
  { id: 'chinese', label: 'Chinese', countries: ['China', 'Taiwan', 'Hong Kong', 'Singapore'] },
  { id: 'japanese', label: 'Japanese', countries: ['Japan'] },
  { id: 'korean', label: 'Korean', countries: ['South Korea', 'Korea'] },
  { id: 'indian', label: 'Indian', countries: ['India', 'Pakistan', 'Bangladesh', 'Sri Lanka', 'Nepal'] },
  { id: 'mexican', label: 'Mexican/Latin American', countries: ['Mexico', 'Brazil', 'Argentina', 'Colombia', 'Peru', 'Chile'] },
  { id: 'italian', label: 'Italian', countries: ['Italy'] },
  { id: 'french', label: 'French', countries: ['France', 'Belgium', 'Switzerland'] },
  { id: 'mediterranean', label: 'Mediterranean', countries: ['Spain', 'Greece', 'Turkey', 'Portugal', 'Morocco'] },
  { id: 'middle_eastern', label: 'Middle Eastern', countries: ['Saudi Arabia', 'UAE', 'Lebanon', 'Israel', 'Iran', 'Egypt'] },
  { id: 'thai', label: 'Thai/Southeast Asian', countries: ['Thailand', 'Vietnam', 'Malaysia', 'Indonesia', 'Philippines'] },
  { id: 'german', label: 'German/Central European', countries: ['Germany', 'Austria', 'Poland', 'Czech Republic', 'Hungary'] },
  { id: 'russian', label: 'Russian/Eastern European', countries: ['Russia', 'Ukraine', 'Belarus'] },
];

export const getDefaultCuisine = (country) => {
  if (!country) return 'western';
  const cuisine = CUISINES.find(c => c.countries.some(cc => country.toLowerCase().includes(cc.toLowerCase())));
  return cuisine ? cuisine.id : 'western';
};

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'zh', label: '中文 (Chinese)', flag: '🇨🇳' },
  { code: 'es', label: 'Español (Spanish)', flag: '🇪🇸' },
  { code: 'pt', label: 'Português (Portuguese)', flag: '🇧🇷' },
  { code: 'ru', label: 'Русский (Russian)', flag: '🇷🇺' },
  { code: 'de', label: 'Deutsch (German)', flag: '🇩🇪' },
  { code: 'fr', label: 'Français (French)', flag: '🇫🇷' },
  { code: 'ko', label: '한국어 (Korean)', flag: '🇰🇷' },
  { code: 'ja', label: '日本語 (Japanese)', flag: '🇯🇵' },
];

export const TRAINING_EVENTS = [
  { id: 'none', label: 'No Specific Event', desc: 'General fitness training', icon: '💪' },
  { id: 'hyrox', label: 'Hyrox', desc: 'Functional fitness racing', icon: '🏋️' },
  { id: 'crossfit', label: 'CrossFit Competition', desc: 'High-intensity varied training', icon: '🔥' },
  { id: '5k', label: '5K Run', desc: 'Speed & endurance focus', icon: '🏃' },
  { id: '10k', label: '10K Run', desc: 'Aerobic conditioning', icon: '🏃‍♂️' },
  { id: 'half_marathon', label: 'Half Marathon', desc: '21.1km distance prep', icon: '🎽' },
  { id: 'marathon', label: 'Marathon', desc: '42.2km ultimate endurance', icon: '🏅' },
  { id: 'ultra_marathon', label: 'Ultra Marathon', desc: 'Beyond the distance', icon: '⛰️' },
  { id: 'triathlon_sprint', label: 'Sprint Triathlon', desc: 'Swim/Bike/Run short course', icon: '🏊' },
  { id: 'triathlon_olympic', label: 'Olympic Triathlon', desc: 'Standard distance tri', icon: '🚴' },
  { id: 'ironman', label: 'Ironman', desc: 'Ultimate endurance challenge', icon: '🦾' },
  { id: 'tough_mudder', label: 'Tough Mudder', desc: 'Obstacle course mastery', icon: '🧗' },
  { id: 'spartan_race', label: 'Spartan Race', desc: 'Strength meets grit', icon: '⚔️' },
  { id: 'true_grit', label: 'True Grit', desc: 'Australian OCR challenge', icon: '💥' },
  { id: 'bodybuilding', label: 'Bodybuilding Competition', desc: 'Physique competition prep', icon: '🏆' },
  { id: 'powerlifting', label: 'Powerlifting Meet', desc: 'Squat/Bench/Deadlift max', icon: '🏋️‍♂️' },
  { id: 'other', label: 'Other Event', desc: 'Custom event training', icon: '✨' },
];

export const calculateWeeksUntilEvent = (eventDate) => {
  if (!eventDate) return null;
  const now = new Date();
  const event = new Date(eventDate);
  const diffTime = event - now;
  const diffWeeks = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
  return diffWeeks > 0 ? diffWeeks : 0;
};

